import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import { Search, Add } from '@mui/icons-material';
import {
  Button,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box
} from '@mui/material';
import AccountHeadModal from './AccountHeadModal';
import Pagination from '../../common/Pagination';

const AccountHeadIndex = () => {
  useSessionValidation();
  const { companyId, token, userId } = useAuth();
  const { canCreateMaster, canEditMaster, canDeleteMaster } = usePermissions();

  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [accountHeads, setAccountHeads] = useState([]);
  const [balanceSheetHeads, setBalanceSheetHeads] = useState([]);
  const [mainHeads, setMainHeads] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterBalanceSheetHead, setFilterBalanceSheetHead] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({});

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [loadingVariant, setLoadingVariant] = useState('default');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const accountHeadApiEndpoint = 'IncomeExpenseApi';
  const balanceSheetApiEndpoint = 'BalanceSheetHeadApi';
  const incomeExpenseApiEndpoint = 'IncomeExpenseApi';

  useEffect(() => {
    if (companyId && token) {
      fetchAllData();
    }
  }, [companyId, token]);

  const fetchAccountHeads = async () => {
    try {
      const response = await fetchData(accountHeadApiEndpoint, `/${companyId}`, token);
      const data = response.response_data || response || [];

      // Remove duplicates based on Id
      const uniqueData = data.filter((item, index, self) =>
        index === self.findIndex((t) => t.Id === item.Id)
      );

      setAccountHeads(uniqueData);
    } catch (error) {
      console.error('Error fetching account heads:', error);
      setAccountHeads([]);
    }
  };

  const fetchBalanceSheetHeads = async () => {
    try {
      const response = await fetchData(balanceSheetApiEndpoint, `/${companyId}`, token);
      const data = response.response_data || response || [];
      setBalanceSheetHeads(data);
    } catch (error) {
      console.error('Error fetching balance sheet heads:', error);
      setBalanceSheetHeads([]);
    }
  };

  const fetchMainHeads = async () => {
    try {
      const response = await fetchData(incomeExpenseApiEndpoint, `/MainHead/${companyId}`, token);
      const data = response.response_data || response || [];
      setMainHeads(data);
    } catch (error) {
      console.error('Error fetching main heads:', error);
      setMainHeads([]);
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Fetching account heads...');
      setLoadingVariant('default');
      await Promise.allSettled([fetchAccountHeads(), fetchBalanceSheetHeads(), fetchMainHeads()]);
      setLoadingMessage('Data loaded successfully!');
      setLoadingVariant('success');
      setTimeout(() => setLoading(false), 800);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadingMessage('Failed to load data!');
      setLoadingVariant('error');
      setError('Failed to fetch data. Please try again.');
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const fetchSingleItem = async (id) => {
    try {
      const response = await fetchData(accountHeadApiEndpoint, `/GetById/${id}`, token);
      return response.response_data || response || null;
    } catch (error) {
      console.error('Error fetching account head by ID:', error);
      return null;
    }
  };

  const handleOpenModal = async (action, item = null) => {
    setModalAction(action);
    if ((action === 'view' || action === 'edit') && item?.Id) {
      setLoading(true);
      setLoadingMessage('Fetching details...');
      setLoadingVariant('default');
      const freshData = await fetchSingleItem(item.Id);
      if (freshData) {
        setSelectedItem({ ...freshData, type: 'accountHead' });
        setFormData({ ...freshData, type: 'accountHead' });
      } else {
        setSelectedItem({ ...item, type: 'accountHead' });
        setFormData({ ...item, type: 'accountHead' } || {});
      }
      setLoading(false);
    } else {
      setSelectedItem({ type: 'accountHead' });
      setFormData({ type: 'accountHead' });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    window.location.reload();

  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} account head...`);
      setLoadingVariant('default');

      let dataToSend = { ...formData, CompanyId: companyId };
      delete dataToSend.type;

      if (dataToSend.MainHeadId) dataToSend.MainHeadId = parseInt(dataToSend.MainHeadId) || null;
      if (dataToSend.BalanceSheetHeadId) dataToSend.BalanceSheetHeadId = parseInt(dataToSend.BalanceSheetHeadId) || null;
      if (dataToSend.VATRate) dataToSend.VATRate = parseFloat(dataToSend.VATRate) || 0;
      dataToSend.IsExpenseOrIncome = Boolean(dataToSend.IsExpenseOrIncome);
      dataToSend.IsTrading = Boolean(dataToSend.IsTrading);

      // Check for duplicate combination
      const isDuplicate = accountHeads.some(item => {
        // Skip the current item when editing
        if (modalAction === 'edit' && item.Id === (selectedItem?.Id || formData?.Id)) {
          return false;
        }

        return (
          item.Name?.toLowerCase() === dataToSend.Name?.toLowerCase() &&
          item.BalanceSheetHeadId === dataToSend.BalanceSheetHeadId &&
          item.VATRate === dataToSend.VATRate &&
          item.IsExpenseOrIncome === dataToSend.IsExpenseOrIncome
        );
      });

      if (isDuplicate) {
        setError('An account head with the same Name, Balance Sheet, VAT Rate, and Income/Expense type already exists!');
        setLoading(false);
        return;
      }

      if (modalAction === 'create') {
        dataToSend.CreatedBy = userId;
        await postData(accountHeadApiEndpoint, '', dataToSend, token);
        setSuccess('Account head created successfully!');
      } else if (modalAction === 'edit') {
        const itemId = selectedItem?.Id || formData?.Id;
        if (!itemId) throw new Error('Item ID is missing');
        dataToSend.Id = itemId;
        dataToSend.ModifiedBy = userId;
        await updateData(accountHeadApiEndpoint, itemId, dataToSend, token);
        setSuccess('Account head updated successfully!');
      }

      handleCloseModal();
      await fetchAccountHeads();
      setLoading(false);
      window.location.reload();

    } catch (error) {
      console.error('Error saving:', error);
      setError('Failed to save account head. Please try again.');
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteItem(item);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await deleteData(accountHeadApiEndpoint, deleteItem.Id, token);
      const filteredData = accountHeads.filter(i => i.Id !== deleteItem.Id);
      setAccountHeads(filteredData);
      setSuccess('Account head deleted successfully!');
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
      setTimeout(() => setLoading(false), 800);
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete account head. Please try again.');
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeleteItem(null);
  };

  const handleActivate = async (item) => {
    try {
      setLoading(true);
      await activateData(accountHeadApiEndpoint, item.Id, token);
      const updatedData = accountHeads.map(i => i.Id === item.Id ? { ...i, IsActive: true } : i);
      setAccountHeads(updatedData);
      setSuccess('Account head activated successfully!');
      setTimeout(() => setLoading(false), 800);
    } catch (error) {
      console.error('Error activating:', error);
      setError('Failed to activate account head. Please try again.');
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const getFilteredData = () => {
    let filtered = accountHeads;
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.Description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.SearchableName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterActive !== 'all') {
      filtered = filtered.filter(item => filterActive === 'active' ? item.IsActive : !item.IsActive);
    }
    if (filterBalanceSheetHead !== 'all') {
      filtered = filtered.filter(item => item.BalanceSheetHeadId === parseInt(filterBalanceSheetHead));
    }
    return filtered;
  };

  const getPaginatedData = () => {
    const filtered = getFilteredData();
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return {
      data: filtered.slice(startIndex, endIndex),
      totalCount: filtered.length,
      totalPages: Math.ceil(filtered.length / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(Number(value));
    setCurrentPage(1);
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData();

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Romans' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase', color: 'black' }}>
            Account Head
          </h4>
        </div>

        {/* Search and Create Button */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, description..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: '#0f3460', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
                '&:hover fieldset': { borderColor: '#0f3460' },
                '&.Mui-focused fieldset': { borderColor: '#0f3460' },
              },
            }}
          />
          {canCreateMaster && (
            <Tooltip title="Create a new account head" arrow>
              <Button
                variant="contained"
                onClick={() => handleOpenModal('create')}
                sx={{
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  color: 'white',
                  padding: { xs: '6px', sm: '4px 12px' },
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '700',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                  minHeight: '29px',
                  minWidth: { xs: '29px', sm: 'auto' },
                  textTransform: 'none',
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap',
                  '& .button-text': {
                    display: { xs: 'none', sm: 'inline' }
                  }
                }}
              >
                <Add sx={{ fontSize: '16px' }} />
                <span className="button-text" style={{ marginLeft: '4px' }}>Create Account Head</span>
              </Button>
            </Tooltip>
          )}
        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', height: '27px' }}>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px' }}>Sl No.</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px' }}>Name</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px' }}>Balance Sheet</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px' }}>Main Head</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px' }}>VAT Rate</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px' }}>SAC Code</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px' }}>Income/Expense</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px' }}>Trading</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px' }}>Status</TableCell>
                  <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', textAlign: 'right', height: '27px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{startIndex + index + 1}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.Name || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{balanceSheetHeads.find(bs => bs.Id === item.BalanceSheetHeadId)?.Name || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.MainHeadId ? mainHeads.find(h => h.Id === item.MainHeadId)?.Name || '-' : '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.VATRate ? `${item.VATRate}%` : '0%'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.SACCode || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      {(() => {
                        const balanceSheetName = balanceSheetHeads.find(bs => bs.Id === item.BalanceSheetHeadId)?.Name || '';
                        const isExpense = balanceSheetName.toLowerCase().includes('expense') || item.IsExpenseOrIncome;
                        return <Chip label={isExpense ? 'Expense' : 'Income'} color={isExpense ? 'warning' : 'info'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />;
                      })()}
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      <Chip label={item.IsTrading ? 'Yes' : 'No'} color={item.IsTrading ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      <Chip label={item.IsActive ? 'Active' : 'Inactive'} color={item.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    </TableCell>
                    <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Tooltip title="View Details" arrow>
                          <Button size="small" onClick={() => handleOpenModal('view', item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button>
                        </Tooltip>
                        {canEditMaster && (
                          <Tooltip title="Edit Account Head" arrow>
                            <Button size="small" onClick={() => handleOpenModal('edit', item)} disabled={!item.IsActive || item.Id < 100} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✏️</Button>
                          </Tooltip>
                        )}
                        {canDeleteMaster && (
                          <Tooltip title="Delete Account Head" arrow>
                            <Button size="small" onClick={() => handleDeleteClick(item)} disabled={item.Id < 100} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🗑️</Button>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalCount={totalCount}
          startIndex={startIndex}
        />
      </div>

      <LoadingOverlay open={loading} message={loadingMessage} variant={loadingVariant} />

      <AccountHeadModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        balanceSheetHeads={balanceSheetHeads}
        mainHeads={mainHeads}
      />

      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '16px' }}>Confirm Delete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#4a5568' }}>Are you sure you want to delete "{deleteItem?.Name}"?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} sx={{ textTransform: 'none', color: '#718096' }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: '6px' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error || !!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={error ? 'error' : 'success'}>{error || success}</Alert>
      </Snackbar>
    </div>
  );
};

export default AccountHeadIndex;
